import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Download, Trash2, Shield, Database, AlertTriangle,
  Loader2, CheckCircle, FileJson, X, AlertCircle, Info
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DataPrivacySection = ({ userId, userType = 'customer' }) => {
  const { t } = useTranslation();
  const [dataSummary, setDataSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteForm, setDeleteForm] = useState({
    password: '',
    confirmation: '',
    reason: ''
  });
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchDataSummary();
  }, [userId]);

  const fetchDataSummary = async () => {
    try {
      const { data } = await axios.get(`${API}/gdpr/data-summary`, {
        headers: { 'X-User-Id': userId }
      });
      setDataSummary(data);
    } catch (error) {
      console.error('Error fetching data summary:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportData = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/gdpr/export-data`, {
        headers: { 'X-User-Id': userId },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `lokalgrow_mis_datos_${new Date().toISOString().split('T')[0]}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(t('gdpr.export_success') || 'Datos exportados correctamente');
    } catch (error) {
      toast.error(t('gdpr.export_error') || 'Error exportando datos');
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteForm.confirmation !== 'ELIMINAR') {
      toast.error(t('gdpr.delete_confirm_error') || 'Escribe ELIMINAR para confirmar');
      return;
    }
    
    if (!deleteForm.password) {
      toast.error(t('gdpr.password_required') || 'Introduce tu contraseña');
      return;
    }
    
    setDeleting(true);
    try {
      await axios.post(`${API}/gdpr/delete-account`, {
        password: deleteForm.password,
        confirmation: deleteForm.confirmation,
        reason: deleteForm.reason
      }, {
        headers: { 'X-User-Id': userId }
      });
      
      toast.success(t('gdpr.delete_success') || 'Cuenta eliminada correctamente');
      
      // Clear local storage and redirect
      localStorage.clear();
      setTimeout(() => {
        window.location.href = '/';
      }, 2000);
      
    } catch (error) {
      const message = error.response?.data?.detail || t('gdpr.delete_error') || 'Error eliminando cuenta';
      toast.error(message);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 bg-local-sage/10 rounded-xl">
          <Shield className="w-6 h-6 text-local-sage" />
        </div>
        <div>
          <h2 className="text-xl font-bold">{t('gdpr.title') || 'Datos y Privacidad'}</h2>
          <p className="text-sm text-muted-foreground">
            {t('gdpr.subtitle') || 'Gestiona tus datos personales según el RGPD'}
          </p>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800 dark:text-blue-200">
            <p className="font-medium mb-1">{t('gdpr.info_title') || 'Tus derechos RGPD'}</p>
            <p>
              {t('gdpr.info_text') || 'Tienes derecho a acceder, descargar y eliminar tus datos personales en cualquier momento. Estos derechos están protegidos por el Reglamento General de Protección de Datos (RGPD) de la Unión Europea.'}
            </p>
          </div>
        </div>
      </div>

      {/* Data Summary */}
      {dataSummary && (
        <div className="bg-card rounded-2xl p-6 border border-border/50">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Database className="w-5 h-5 text-local-sage" />
            {t('gdpr.data_summary') || 'Resumen de tus datos'}
          </h3>
          
          <div className="space-y-3">
            {dataSummary.data_categories.map((category, idx) => (
              <div 
                key={idx}
                className="flex items-center justify-between p-3 bg-muted/50 rounded-xl"
              >
                <div>
                  <p className="font-medium">{category.label}</p>
                  <p className="text-sm text-muted-foreground">{category.description}</p>
                </div>
                <span className="px-3 py-1 bg-local-sage/10 text-local-sage rounded-full text-sm font-medium">
                  {category.count}
                </span>
              </div>
            ))}
          </div>
          
          <p className="text-xs text-muted-foreground mt-4">
            {t('gdpr.account_created') || 'Cuenta creada'}: {new Date(dataSummary.created_at).toLocaleDateString('es-ES', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </p>
        </div>
      )}

      {/* Export Data */}
      <div className="bg-card rounded-2xl p-6 border border-border/50">
        <h3 className="font-semibold mb-2 flex items-center gap-2">
          <Download className="w-5 h-5 text-local-sage" />
          {t('gdpr.export_title') || 'Descargar mis datos'}
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          {t('gdpr.export_description') || 'Descarga una copia completa de todos tus datos en formato JSON. Incluye tu perfil, actividad, reservas y más.'}
        </p>
        
        <Button
          onClick={handleExportData}
          disabled={exporting}
          className="bg-local-sage hover:bg-local-sage/90 rounded-xl"
          data-testid="export-data-btn"
        >
          {exporting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              {t('gdpr.exporting') || 'Exportando...'}
            </>
          ) : (
            <>
              <FileJson className="w-4 h-4 mr-2" />
              {t('gdpr.download_data') || 'Descargar mis datos'}
            </>
          )}
        </Button>
      </div>

      {/* Delete Account */}
      <div className="bg-card rounded-2xl p-6 border border-red-200 dark:border-red-900/50">
        <h3 className="font-semibold mb-2 flex items-center gap-2 text-red-600 dark:text-red-400">
          <Trash2 className="w-5 h-5" />
          {t('gdpr.delete_title') || 'Eliminar mi cuenta'}
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          {t('gdpr.delete_description') || 'Elimina permanentemente tu cuenta y anonimiza todos tus datos personales. Esta acción no se puede deshacer.'}
        </p>
        
        <Button
          variant="destructive"
          onClick={() => setShowDeleteModal(true)}
          className="rounded-xl"
          data-testid="delete-account-btn"
        >
          <Trash2 className="w-4 h-4 mr-2" />
          {t('gdpr.delete_account') || 'Eliminar mi cuenta'}
        </Button>
      </div>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
            onClick={() => setShowDeleteModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-card rounded-2xl p-6 w-full max-w-md shadow-xl max-h-[90vh] overflow-y-auto"
              data-testid="delete-account-modal"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-lg flex items-center gap-2 text-red-600">
                  <AlertTriangle className="w-5 h-5" />
                  {t('gdpr.delete_confirm_title') || 'Confirmar eliminación'}
                </h3>
                <button onClick={() => setShowDeleteModal(false)}>
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 mb-6">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-red-800 dark:text-red-200">
                    <p className="font-medium mb-1">
                      {t('gdpr.delete_warning_title') || 'Esta acción es irreversible'}
                    </p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>{t('gdpr.delete_warning_1') || 'Tu cuenta será desactivada permanentemente'}</li>
                      <li>{t('gdpr.delete_warning_2') || 'Tus datos personales serán anonimizados'}</li>
                      <li>{t('gdpr.delete_warning_3') || 'Perderás todos tus puntos y recompensas'}</li>
                      {userType === 'business' && (
                        <li>{t('gdpr.delete_warning_business') || 'Todos tus kupones serán desactivados'}</li>
                      )}
                    </ul>
                  </div>
                </div>
              </div>
              
              <div className="space-y-4">
                <div>
                  <Label>{t('gdpr.password') || 'Contraseña actual'}</Label>
                  <Input
                    type="password"
                    value={deleteForm.password}
                    onChange={(e) => setDeleteForm({...deleteForm, password: e.target.value})}
                    placeholder="••••••••"
                    className="rounded-xl"
                    data-testid="delete-password-input"
                  />
                </div>
                
                <div>
                  <Label>
                    {t('gdpr.confirm_type') || 'Escribe'} <span className="font-bold text-red-600">ELIMINAR</span> {t('gdpr.confirm_type_2') || 'para confirmar'}
                  </Label>
                  <Input
                    type="text"
                    value={deleteForm.confirmation}
                    onChange={(e) => setDeleteForm({...deleteForm, confirmation: e.target.value.toUpperCase()})}
                    placeholder="ELIMINAR"
                    className="rounded-xl"
                    data-testid="delete-confirmation-input"
                  />
                </div>
                
                <div>
                  <Label>{t('gdpr.reason') || 'Motivo (opcional)'}</Label>
                  <textarea
                    value={deleteForm.reason}
                    onChange={(e) => setDeleteForm({...deleteForm, reason: e.target.value})}
                    placeholder={t('gdpr.reason_placeholder') || '¿Por qué deseas eliminar tu cuenta?'}
                    className="w-full min-h-[80px] px-3 py-2 bg-background border border-input rounded-xl resize-none text-sm"
                    data-testid="delete-reason-input"
                  />
                </div>
                
                <div className="flex gap-3 pt-2">
                  <Button
                    variant="outline"
                    onClick={() => setShowDeleteModal(false)}
                    className="flex-1 rounded-xl"
                  >
                    {t('cancel') || 'Cancelar'}
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={handleDeleteAccount}
                    disabled={deleting || deleteForm.confirmation !== 'ELIMINAR'}
                    className="flex-1 rounded-xl"
                    data-testid="confirm-delete-btn"
                  >
                    {deleting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {t('gdpr.deleting') || 'Eliminando...'}
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4 mr-2" />
                        {t('gdpr.confirm_delete') || 'Eliminar cuenta'}
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default DataPrivacySection;
